import Relay from "react-relay"
import { filter } from "lodash/collection"
import { getPublicUrl } from "./utils"
import { HELD_IN_NAMES } from "../constants"
import { getAccountSettings } from "../scripts/accountSettings"

const TYPE_IMAGE = "image"
const TYPE_TRANSCRIPTION = "transcription"
const TYPE_BOOK = "book"
const TYPE_AUDIO = "audio"
const TYPE_VIDEO = "video"

export function getFileByType(files, type) {
  if (files && files.length) {
    const found = filter(files, { type: type })

    if (found && found.length) {
      return found[0]
    }
  }
}

export function getDeepZoomSrcFromResource(resource) {
  if (resource.files && resource.files.length) {
    const file = getFileByType(resource.files, "image")

    return file && file.deep_zoom && file.deep_zoom.url
  }

  return false
}

export function getDeepZoomSrcFromFile(file) {
  return file && file.deep_zoom && file.deep_zoom.url
}

export function getImagesFromResource(resource, type = TYPE_IMAGE) {
  if (resource.files && resource.files.length) {
    const img = getFileByType(resource.files, type)

    return img ? img.images : []
  }

  return []
}

export function getImagesFromFile(file) {
  if (file.images) {
    return file.images || []
  }

  return []
}

export function getImageSrcFromResource(
  resource,
  size = "medium",
  type = TYPE_IMAGE,
  placeholder = true
) {
  const image = getImageFromResource(resource, size, type)

  return image ? image.url : placeholder && getPlaceholder(resource).small
}

export function getImageSrcFromFile(
  file,
  size = "medium",
  type = TYPE_IMAGE,
  placeholder
) {
  const image = getImageFromFile(file, size, type)

  return image
    ? image.url
    : placeholder
      ? "/images/placeholders/default05.jpg?v=1.2.3"
      : ""
}

export function getImageFromResource(
  resource,
  size = "medium",
  type = TYPE_IMAGE
) {
  const images = getImagesFromResource(resource, type)

  return images && images[size]
}

export function getImageFromFile(file, size = "medium") {
  const images = getImagesFromFile(file)

  return images && images[size]
}

const placeholderFolder = "/images/placeholders/"
const placeholders = {
  default: {
    ext: "jpg",
    values: Array.from(Array(5)).map(
      (_, k) => "default" + (k < 10 ? "0" + (k + 1) : k + 1)
    )
  },
  type1: {
    ext: "png",
    values: [
      "Default_article",
      "Default_audio",
      "Default_book",
      "Default_image",
      "Default_multi_page",
      "Default_video_collections"
    ]
  }
}

export function getPlaceholder(resource, authenticAccountId) {
  const {
    settings: {
      style: { content: contentType }
    }
  } = getAccountSettings(authenticAccountId)

  // info: add new type here
  if (!["default", "type1"].includes(contentType)) {
    return ""
  }

  const defaultPlaceholder =
    placeholders[contentType].values[
      placeholders[contentType].values.length - 1
    ]

  // set the last placeholder as default
  let imageName = defaultPlaceholder

  if (contentType === "type1") {
    // find placeholder by resource type
    placeholders[contentType].values.forEach(i => {
      if (i.indexOf(resource.type) !== -1) {
        imageName = i
        return
      }
    })
  } else {
    // generate a random placeholder depends on title length
    let title = resource
    if (typeof resource !== "string") {
      // handle legacy resource param
      title = resource.title
    }

    imageName =
      placeholders[contentType].values[
        title.length % placeholders[contentType].values.length
      ] || defaultPlaceholder
  }

  return {
    small: `${placeholderFolder}${imageName}.${placeholders[contentType].ext}`,
    retina: `${placeholderFolder}${imageName}@2x.${
      placeholders[contentType].ext
    }`
  }
}

export function createSrcSetForResource(resource, type = TYPE_IMAGE) {
  const images = getImagesFromResource(resource, type)

  const srcSet = images
    ? ["small", "medium", "large"].reduce((srcSet, size) => {
        if (size in images) {
          srcSet.push(images[size].url + " " + images[size].width + "w")
        }
        return srcSet
      }, [])
    : []

  return srcSet
}

export function createSrcSetForFile(file, type = TYPE_IMAGE) {
  const images = getImagesFromFile(file, type)

  const srcSet = images
    ? ["small", "medium", "large"].reduce((srcSet, size) => {
        if (size in images) {
          srcSet.push(images[size].url + " " + images[size].width + "w")
        }
        return srcSet
      }, [])
    : []

  return srcSet
}

export function createSrcSetFromCollection(images) {
  return ["small", "medium", "large"].reduce((prev, curr) => {
    if (curr in images) {
      prev.push(images[curr].url + " " + images[curr].width + "w")
    }

    return prev
  }, [])
}

export function getAudioSrcFromResource(resource, format = "lossy") {
  const audio = getAudioFromResource(resource, format)

  return audio && "url" in audio && audio.url
}

export function getAudioSrcFromFile(file, format = "lossy") {
  const audio = getAudioFromFile(file, format)

  return audio && "url" in audio && audio.url
}

export function getAudioFromResource(resource, format = "lossy") {
  const file = getFileByType(resource.files, TYPE_AUDIO)

  return file && file.audio && format in file.audio && file.audio[format]
}

export function getAudioFromFile(file, format = "lossy") {
  return file && file.audio && format in file.audio && file.audio[format]
}

export function getVideoFromResource(resource, version = "med") {
  const file = getFileByType(resource.files, TYPE_VIDEO)

  if (!file) {
    return null
  }

  if (!file.video[version]) {
    version = "low"
  }

  return file.video && version in file.video && file.video[version]
}

export function getVideoFromFile(file, version = "med") {
  if (!file) {
    return null
  }

  if (!file.video[version]) {
    version = "low"
  }

  return file.video && version in file.video && file.video[version]
}

export function getTranscript(resource) {
  const file = getFileByType(resource.files, TYPE_TRANSCRIPTION)

  return file && file.contents
}

export function getBookFromResource(resource) {
  const file = getFileByType(resource.files, TYPE_BOOK)

  return file
}

export function getPDFSrcFromResource(resource) {
  const file = getBookFromResource(resource)

  return file && file.documents && file.documents.pdf && file.documents.pdf.url
}

export function getPDFSrcFromFile(file) {
  return file && file.documents && file.documents.pdf && file.documents.pdf.url
}

export function getPermalink(resource) {
  if (!resource) {
    return ""
  }
  return getPublicUrl("item/" + resource.id)
}

//Should pass a string resource type to this, passing a resource object is depreciated now;
export function getImageType(resourceType) {
  resourceType =
    typeof resourceType === "string"
      ? resourceType
      : resourceType && resourceType.type

  switch (resourceType) {
    case "video":
    case "audio":
    case "collection":
      return "cover_image"
    default:
      return "image"
  }
}

export function getHeldInName(slug) {
  return slug in HELD_IN_NAMES ? HELD_IN_NAMES[slug] : `"${slug}"`
}

export function getImageData(resource) {
  const src =
    getImageSrcFromResource(
      resource,
      "small",
      getImageType(resource.type),
      false
    ) ||
    JSON.parse("custom_fields" in resource ? resource.custom_fields : "{}")
      .cover_image_link ||
    ("cover_image" in resource &&
      resource.cover_image &&
      resource.cover_image.medium.url)

  if (src) {
    const imageFile = getFileByType(resource.files, getImageType(resource.type))

    const width = imageFile ? imageFile.width : false
    const height = imageFile ? imageFile.height : false

    return {
      src: src,
      aspect: height ? width && (height / width) * 100 : 0,
      title: resource.title
    }
  }
}

export const resourceFileFragment = () => Relay.QL`
  fragment on Resource {
    id
    type
    title
    custom_fields
    type_icon
    type_icon_color
    files {
      id
      width
      height
      type
      images {
        small {
          url
          width
          height
        }
      }
    }
  }
`



// WEBPACK FOOTER //
// ./webapp/scripts/resource.js